import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { restClient, getApiError } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { schoolService } from '@/api/services/schoolService'
import { useLanguages, useCities } from '@/api/hooks'
import { Eye, EyeOff, Info } from 'lucide-react'

const STATUS_OPTIONS = [
  { value: '', label: 'Select Status' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'pending_for_verification', label: 'Pending for Verification' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
]

const empty = {
  locale: 'en',
  name: '',
  about: '',
  contact_email: '',
  contact_phone_number: '',
  city_id: '',
  status: '',
  certificate_commission_in_percentage: '',
  min_commision: '',
  max_commision: '',
  certificate_min_commision: '',
  certificate_max_commision: '',
  is_active: true,
  create_new_user: false,
  user_id: '',
  user_name: '',
  user_email: '',
  password: '',
  phone: '',
}

export function SchoolCreatePage() {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { id: editId } = useParams<{ id: string }>()
  const isEdit = !!editId
  const [form, setForm] = useState({ ...empty })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState('')
  const [saving, setSaving] = useState(false)
  const [showPwd, setShowPwd] = useState(false)
  const [adminSearch, setAdminSearch] = useState('')

  const set = (k: keyof typeof empty, v: any) => setForm(f => ({ ...f, [k]: v }))

  // ── Edit mode: load the school and pre-fill the form ──
  const { data: editData } = useQuery({
    queryKey: ['school-edit', editId],
    queryFn: () => schoolService.getById(editId!).then((r: any) => r.data ?? r),
    enabled: isEdit,
  })
  useEffect(() => {
    if (!editData) return
    const tr = editData.vendor_translations?.find((t: any) => t.locale?.toLowerCase() === 'en')
      ?? editData.vendor_translations?.[0] ?? {}
    setForm(f => ({
      ...f,
      locale:   tr.locale ?? 'en',
      name:     tr.name ?? '',
      about:    tr.about ?? '',
      contact_email:        editData.email ?? editData.contact_email ?? '',
      contact_phone_number: editData.phone_number ?? editData.contact_phone_number ?? '',
      city_id:  String(editData.city_id ?? editData.city?.id ?? editData.address?.city_id ?? ''),
      status:   editData.status ?? '',
      is_active: !!editData.is_active,
      min_commision: editData.min_commision != null ? String(editData.min_commision) : '',
      max_commision: editData.max_commision != null ? String(editData.max_commision) : '',
      certificate_commission_in_percentage: editData.certificate_commission_in_percentage != null ? String(editData.certificate_commission_in_percentage) : '',
      certificate_min_commision: editData.certificate_min_commision != null ? String(editData.certificate_min_commision) : '',
      certificate_max_commision: editData.certificate_max_commision != null ? String(editData.certificate_max_commision) : '',
      // Editing keeps the existing admin — never create a new one.
      create_new_user: false,
      user_id: String(editData.vendor_user?.user_id ?? editData.vendor_user?.user?.id ?? editData.user_id ?? ''),
    }))
  }, [editData])

  // ── City options ──
  const { data: cityData } = useCities({ limit: 200 })
  const cities: any[] = (cityData as any)?.data ?? []
  const cityName = (c: any) =>
    c?.city_translations?.find((tr: any) => tr?.locale?.toLowerCase() === 'en')?.name
    ?? c?.city_translations?.[0]?.name
    ?? c?.name
    ?? `City #${c?.id}`

  // ── Locale options ──
  const { data: langData } = useLanguages({ limit: 1000 })
  const languages: any[] = (langData as any)?.data ?? (langData as any) ?? []
  const localeOptions = languages
    .map((l: any) => l.language_culture ?? l.locale ?? l.code)
    .filter(Boolean)

  // ── School admin options (when selecting an existing admin) ──
  const { data: adminData, isLoading: adminLoading } = useQuery({
    queryKey: ['school-admins', adminSearch],
    queryFn: () => restClient
      .get(`${endpoints.school.getSchoolAdmin}?page=1&limit=1000${adminSearch ? `&search=${encodeURIComponent(adminSearch)}` : ''}`)
      .then(r => r.data),
    enabled: !form.create_new_user,
    staleTime: 30_000,
  })
  const admins: any[] = (adminData as any)?.data ?? []

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Name is required'
    if (!form.locale) e.locale = 'Locale is required'
    if (!form.city_id) e.city_id = 'City is required'
    if (form.contact_email && !/^[^@]+@[^@]+\.[^@]+$/.test(form.contact_email)) e.contact_email = 'Invalid email format'
    if (form.contact_phone_number && !/^\d{1,9}$/.test(String(form.contact_phone_number))) e.contact_phone_number = 'Contact number must be up to 9 digits'
    if (form.certificate_commission_in_percentage !== '' && Number.isNaN(Number(form.certificate_commission_in_percentage))) e.certificate_commission_in_percentage = 'Commission percentage must be a number'

    const cMin = Number(form.certificate_min_commision)
    const cMax = Number(form.certificate_max_commision)
    if (form.certificate_min_commision === '' || Number.isNaN(cMin)) e.certificate_min_commision = 'Min certificate commission is required'
    else if (cMin < 0 || cMin > 100) e.certificate_min_commision = 'Must be between 0 and 100'
    if (form.certificate_max_commision === '' || Number.isNaN(cMax)) e.certificate_max_commision = 'Max certificate commission is required'
    else if (cMax < 0 || cMax > 100) e.certificate_max_commision = 'Must be between 0 and 100'
    else if (!Number.isNaN(cMin) && cMax < cMin) e.certificate_max_commision = 'Max must be greater than min'

    // Editing keeps the existing admin, so skip admin-selection checks entirely.
    if (!isEdit) {
      if (form.create_new_user) {
        if (!form.user_name.trim()) e.user_name = 'User name is required'
        if (!form.user_email.trim()) e.user_email = 'User email is required'
        else if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.user_email)) e.user_email = 'Invalid email format'
        if (!form.password) e.password = 'Password is required'
        if (!/^5\d{8}$/.test(form.phone)) e.phone = 'Phone must be 9 digits starting with 5'
      } else {
        if (!form.user_id) e.user_id = 'Please select a school admin'
      }
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async () => {
    setSubmitError('')
    if (!validate()) return
    if (isEdit && !(await confirm({
      title: t('common.confirmSaveTitle'), description: t('common.confirmSaveDescription'),
      confirmLabel: t('common.saveChanges'), variant: 'primary',
    }))) return
    setSaving(true)
    try {
      const fd = new FormData()
      fd.append('contact_email', form.contact_email)
      fd.append('contact_phone_number', form.contact_phone_number)
      if (form.city_id) fd.append('city_id', String(form.city_id))
      fd.append('min_commision', form.min_commision)
      fd.append('max_commision', form.max_commision)
      fd.append('certificate_min_commision', form.certificate_min_commision)
      fd.append('certificate_max_commision', form.certificate_max_commision)
      fd.append('certificate_commission_in_percentage', form.certificate_commission_in_percentage)
      fd.append('status', form.status)
      fd.append('is_active', form.is_active ? '1' : '0')
      fd.append('create_new_user', form.create_new_user ? '1' : '0')
      if (!form.create_new_user) {
        fd.append('user_id', String(form.user_id))
      } else {
        fd.append('name', form.user_name)
        fd.append('user_email', form.user_email)
        fd.append('password', form.password)
        fd.append('phone', form.phone)
        fd.append('country_code', '971')
      }
      fd.append('vendor_translations[0][name]', form.name)
      fd.append('vendor_translations[0][locale]', form.locale)
      fd.append('vendor_translations[0][about]', form.about)

      // Editing the same vendor is an upsert on the create endpoint — the presence
      // of vendor_id turns the create into an update (mirrors the old dashboard).
      if (isEdit) fd.append('vendor_id', String(editId))

      await schoolService.create(fd)
      qc.invalidateQueries({ queryKey: ['schools'] })
      qc.invalidateQueries({ queryKey: ['school-detail', editId] })
      navigate(isEdit ? `/dashboard/school/${editId}` : '/dashboard/school/list')
    } catch (err) {
      setSubmitError(getApiError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-5 pb-28">
      <PageHeader title={isEdit ? 'Edit School' : 'Register New School'}
        breadcrumbs={[
          { label: 'Dashboard', to: '/dashboard' },
          { label: 'School', to: '/dashboard/school/list' },
          { label: isEdit ? 'Edit School' : 'Register New School' },
        ]}
      />

      {submitError && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600 whitespace-pre-line">{submitError}</div>}

      {/* ── School information ── */}
      <Section title="School Information" subtitle="Basic details and description of the school.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Locale *" error={errors.locale}>
            <select value={form.locale} onChange={e => set('locale', e.target.value)} className={inputCls(errors.locale)}>
              {localeOptions.length === 0 && <option value="en">en</option>}
              {localeOptions.map((l: string) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Name *" error={errors.name}>
              <input value={form.name} onChange={e => set('name', e.target.value)} className={inputCls(errors.name)} placeholder="School name" />
            </Field>
          </div>
          <Field label="Email" error={errors.contact_email}>
            <input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} className={inputCls(errors.contact_email)} />
          </Field>
          <Field label="Contact Number" error={errors.contact_phone_number}>
            <input type="number" value={form.contact_phone_number} onChange={e => set('contact_phone_number', e.target.value)} className={inputCls(errors.contact_phone_number)} placeholder="503445679" />
          </Field>
          <Field label="City *" error={errors.city_id}>
            <select value={form.city_id} onChange={e => set('city_id', e.target.value)} className={inputCls(errors.city_id)}>
              <option value="">Select City</option>
              {cities.map((c: any) => <option key={c.id} value={c.id}>{cityName(c)}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={e => set('status', e.target.value)} className={inputCls()}>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <div className="md:col-span-3">
            <Field label="About">
              <textarea value={form.about} onChange={e => set('about', e.target.value)} rows={4} className={inputCls()} />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 md:col-span-3">
            <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} className="accent-brand-500" />
            Active
          </label>
        </div>
      </Section>

      {/* ── Commission ── */}
      <Section title="Commission" subtitle="Set the school commission range and certificate commission.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Certificate Commission (%)" hint="Drivys commission on each certificate" error={errors.certificate_commission_in_percentage}>
            <input type="number" value={form.certificate_commission_in_percentage} onChange={e => set('certificate_commission_in_percentage', e.target.value)} className={inputCls(errors.certificate_commission_in_percentage)} />
          </Field>
          <Field label="Min School Commission">
            <input type="number" value={form.min_commision} onChange={e => set('min_commision', e.target.value)} className={inputCls()} />
          </Field>
          <Field label="Max School Commission">
            <input type="number" value={form.max_commision} onChange={e => set('max_commision', e.target.value)} className={inputCls()} />
          </Field>
          <Field label="Min Certificate Commission *" error={errors.certificate_min_commision}>
            <input type="number" value={form.certificate_min_commision} onChange={e => set('certificate_min_commision', e.target.value)} className={inputCls(errors.certificate_min_commision)} />
          </Field>
          <Field label="Max Certificate Commission *" error={errors.certificate_max_commision}>
            <input type="number" value={form.certificate_max_commision} onChange={e => set('certificate_max_commision', e.target.value)} className={inputCls(errors.certificate_max_commision)} />
          </Field>
        </div>
      </Section>

      {/* ── School admin (create only — editing keeps the existing admin) ── */}
      {!isEdit && (
      <Section title="School Admin" subtitle="Assign an existing admin or create a new one for this school.">
        <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
          <input type="checkbox" checked={form.create_new_user} onChange={e => set('create_new_user', e.target.checked)} className="accent-brand-500" />
          Create new school admin
        </label>

        {!form.create_new_user ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Search Admin">
              <input
                value={adminSearch}
                onChange={e => setAdminSearch(e.target.value)}
                placeholder="Search admin by name/email…"
                className={inputCls()}
              />
            </Field>
            <Field label="Select School Admin *" error={errors.user_id}>
              <select value={form.user_id} onChange={e => set('user_id', e.target.value)} className={inputCls(errors.user_id)}>
                <option value="">{adminLoading ? 'Loading…' : 'Select admin'}</option>
                {admins.map((a: any) => (
                  <option key={a.id} value={a.id}>{a.name} [{a.email}]</option>
                ))}
              </select>
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="User Name *" error={errors.user_name}>
              <input value={form.user_name} onChange={e => set('user_name', e.target.value)} className={inputCls(errors.user_name)} />
            </Field>
            <Field label="User Email *" error={errors.user_email}>
              <input value={form.user_email} onChange={e => set('user_email', e.target.value)} className={inputCls(errors.user_email)} />
            </Field>
            <Field label="Password *" error={errors.password}>
              <div className="relative">
                <input type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} className={inputCls(errors.password) + ' pr-9'} />
                <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>
            <Field label="Phone (+971) *" error={errors.phone}>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} className={inputCls(errors.phone)} placeholder="5XXXXXXXX" />
            </Field>
          </div>
        )}
      </Section>
      )}

      {/* ── Sticky action bar ── */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-gray-200 px-6 py-3">
        <div className="flex items-center justify-end gap-3 max-w-[1400px] mx-auto">
          <Button variant="outline" onClick={() => navigate(isEdit ? `/dashboard/school/${editId}` : '/dashboard/school/list')}>Cancel</Button>
          <Button loading={saving} onClick={handleSubmit}>{isEdit ? 'Save Changes' : 'Create School'}</Button>
        </div>
      </div>
    </div>
  )
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <Card>
      <div className="mb-4">
        <h3 className="text-sm font-bold text-gray-900">{title}</h3>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </Card>
  )
}

const inputCls = (error?: string) =>
  `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${error ? 'border-red-400 focus:ring-red-300' : 'border-gray-200 focus:ring-brand-400'}`

function Field({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
        {label}
        {hint && <span title={hint}><Info size={11} className="text-gray-400" /></span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  )
}
